<template>
  <div>
    <h2 style="padding-bottom: 20px">Declaration Summary</h2>

    <h4>Your Declaration ID is {{ currentDeclaration.declarationNumber }}</h4>

    <barcode v-bind:value="currentDeclaration.declarationNumber" format="CODE128">
      {{ currentDeclaration.declarationNumber }}
    </barcode>

    <p>
      Please print this page or note down the Declaration ID and provide it to
      your MaltaPost post office.
    </p>
    <p>
      The Declaration ID and the declared information is valid for
      {{ expiresInDays }} days. After {{ expiresInDays }} days the data will be
      deleted.
    </p>

    The following information has been recorded:
    <v-container>
      <v-row>
        <v-col cols="12" md="6">
          <h3 class="summary-header">Sender</h3>
          <div class="text-sm-body-2 text-body-2">
            <v-container>
              <v-row no-gutters>
                <v-col cols="4" md="3">
                  <span> Name: </span>
                </v-col>
                <v-col cols="8" md="8">
                  <span>
                    {{ currentDeclaration.sender.name }}
                    {{ currentDeclaration.sender.surname }}
                  </span>
                </v-col>
              </v-row>
              <v-row no-gutters>
                <v-col cols="4" md="3">
                  <span> Address: </span>
                </v-col>
                <v-col cols="8" md="8">
                  <span>{{ currentDeclaration.sender.addressLine1 }}</span>
                  <span v-if="currentDeclaration.sender.addressLine2"
                    ><br />{{ currentDeclaration.sender.addressLine2 }}</span
                  >
                  <span><br />{{ currentDeclaration.sender.postcode }}</span>
                  <span><br />{{ currentDeclaration.sender.locality }}</span>
                  <span
                    ><br />{{
                      getCountryNameById(currentDeclaration.sender.countryId)
                    }}</span
                  >
                </v-col>
              </v-row>
              <v-row no-gutters v-if="currentDeclaration.sender.email">
                <v-col cols="4" md="3">
                  <span> Email: </span>
                </v-col>
                <v-col cols="8" md="8">
                  <span>
                    {{ currentDeclaration.sender.email }}
                  </span>
                </v-col>
              </v-row>

              <v-row no-gutters v-if="currentDeclaration.sender.phone">
                <v-col cols="4" md="3">
                  <span> Phone: </span>
                </v-col>
                <v-col cols="8" md="8">
                  <span>
                    {{ currentDeclaration.sender.phone }}
                  </span>
                </v-col>
              </v-row>

              <v-row no-gutters v-if="currentDeclaration.sender.vatNumber">
                <v-col cols="4" md="3">
                  <span> Vat No: </span>
                </v-col>
                <v-col cols="8" md="8">
                  <span>
                    {{ currentDeclaration.sender.vatNumber }}
                  </span>
                </v-col>
              </v-row>
              <v-row no-gutters v-if="currentDeclaration.sender.voecNumber && getCountryRequiresVoec(currentDeclaration.recipient.countryId)">
                <v-col cols="4" md="3">
                  <span> VOEC No: </span>
                </v-col>
                <v-col cols="8" md="8">
                  <span>
                    {{ currentDeclaration.sender.voecNumber }}
                  </span>
                </v-col>
              </v-row>

            </v-container>
          </div>
          
          <h3 class="mt-4 summary-header">Consignor</h3>
          <div class="text-sm-body-2 text-body-2">
            <v-container>
              <v-row no-gutters>
                <v-col cols="4" md="3">
                  <span> Name: </span>
                </v-col>
                <v-col cols="8" md="8">
                  <span>
                    {{ currentDeclaration.sender.consignor.name }}
                    {{ currentDeclaration.sender.consignor.surname }}
                  </span>
                </v-col>
              </v-row>

              <v-row no-gutters>
                <v-col cols="4" md="3">
                  <span> Phone: </span>
                </v-col>
                <v-col cols="8" md="8">
                  <span>
                    {{ currentDeclaration.sender.consignor.phone }}
                  </span>
                </v-col>
              </v-row>

              <v-row no-gutters>
                <v-col cols="4" md="3">
                  <span> ID/Passport Number: </span>
                </v-col>
                <v-col cols="8" md="8">
                  <span>
                    {{ currentDeclaration.sender.consignor.idNumber }}
                  </span>
                </v-col>
              </v-row>
              
              <br />

              <v-row no-gutters>
                <v-col cols="12">
                  <em class="grey--text text--darken-1">
                    Note: Consignor ID/Passport number will be requested upon parcel deposit at the postal counter.
                  </em>
                </v-col>
              </v-row>
            </v-container>
          </div>

        </v-col>

        <v-col cols="12" md="6">
          <h3 class="summary-header">Recipient</h3>
          <div class="text-sm-body-2 text-body-2">
            <v-container>
              <v-row no-gutters>
                <v-col cols="4" md="3">
                  <span> Name: </span>
                </v-col>
                <v-col cols="8" md="8">
                  <span>
                    {{ currentDeclaration.recipient.name }}
                    {{ currentDeclaration.recipient.surname }}
                  </span>
                </v-col>
              </v-row>
              <v-row no-gutters>
                <v-col cols="4" md="3">
                  <span> Address: </span>
                </v-col>
                <v-col cols="8" md="8">
                  <span>{{ currentDeclaration.recipient.addressLine1 }}</span>
                  <span v-if="currentDeclaration.recipient.addressLine2"
                    ><br />{{ currentDeclaration.recipient.addressLine2 }}</span
                  >
                  <span><br />{{ currentDeclaration.recipient.postcode }}</span>
                  <span><br />{{ currentDeclaration.recipient.locality }}</span>
                  <span
                    ><br />{{
                      getCountryNameById(currentDeclaration.recipient.countryId)
                    }}</span
                  >
                </v-col>
              </v-row>

              <v-row no-gutters v-if="currentDeclaration.recipient.email">
                <v-col cols="4" md="3">
                  <span> Email: </span>
                </v-col>
                <v-col cols="8" md="8">
                  <span>
                    {{ currentDeclaration.recipient.email }}
                  </span>
                </v-col>
              </v-row>
              <v-row no-gutters v-if="currentDeclaration.recipient.phone">
                <v-col cols="4" md="3">
                  <span> Phone: </span>
                </v-col>
                <v-col cols="8" md="8">
                  <span>
                    {{ currentDeclaration.recipient.phone }}
                  </span>
                </v-col>
              </v-row>
              <v-row no-gutters v-if="currentDeclaration.recipient.vatNumber">
                <v-col cols="4" md="3">
                  <span> Vat No: </span>
                </v-col>
                <v-col cols="8" md="8">
                  <span>
                    {{ currentDeclaration.recipient.vatNumber }}
                  </span>
                </v-col>
              </v-row>
            </v-container>
          </div>
        </v-col>
      </v-row>

      <h3 class="summary-header">Category of Parcel Content</h3>
      <div class="text-sm-body-2 text-body-2">
        <v-container style="margin-bottom: 10px">
          {{ currentDeclaration.contentCategory }}
          <span v-if="requiresContentCategoryDescription"
            >({{ currentDeclaration.contentCategoryDescription }})</span
          >
        </v-container>
      </div>

      <h3 class="summary-header" v-if="currentDeclaration.recipient.isRequiresContentItems">
        Content Items
      </h3>
      <div
        v-if="currentDeclaration.recipient.isRequiresContentItems"
        class="text-sm-body-2 text-body-2"
      >
        <v-expansion-panels class="d-md-none" :hover="true">
          <v-expansion-panel
            v-for="item in currentDeclaration.contentItems"
            :key="item.key"
          >
            <v-expansion-panel-header>{{
              item.description
            }}</v-expansion-panel-header>
            <v-expansion-panel-content>
              <dl class="content-item-list">
                <dt class="font-weight-bold">DESCRIPTION: </dt>
                <dd>{{ item.description }}</dd>

                <dt class="font-weight-bold">HS CODE: </dt>
                <dd>{{ item.hsCode }}</dd>

                <dt class="font-weight-bold">COUNTRY OF ORIGIN: </dt>
                <dd>{{ getCountryNameById(item.countryOfOriginId) }}</dd>

                <dt class="font-weight-bold">TOTAL QUANTITY: </dt>
                <dd>{{ item.quantity }}</dd>

                <dt class="font-weight-bold">TOTAL VALUE (€): </dt>
                <dd>{{ item.value }}</dd>

                <dt class="font-weight-bold">TOTAL NET WEIGHT (grams): </dt>
                <dd>{{ item.weight }}</dd>
              </dl>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
        <v-simple-table class="d-none d-md-block">
          <template v-slot:default>
            <thead>
              <tr>
                <th scope="col" class="text-left">Description</th>
                <th scope="col" class="text-left">HS Code</th>
                <th scope="col" class="text-left">Country of Origin</th>
                <th scope="col" class="text-left">Total Quantity</th>
                <th scope="col" class="text-left">Total Value (&euro;)</th>
                <th scope="col" class="text-left">Total Net Weight (g)</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in currentDeclaration.contentItems"
                :key="item.key"
              >
                <td scope="row">{{ item.description }}</td>
                <td>{{ item.hsCode }}</td>
                <td>{{ getCountryNameById(item.countryOfOriginId) }}</td>
                <td>{{ item.quantity }}</td>
                <td>{{ item.value }}</td>
                <td>{{ item.weight }}</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </div>
    </v-container>

    <v-container>
      <v-row no-gutters>
        <v-col cols="12" sm="4" class="pa-2">
          <v-btn
            color="primary"
            block
            class="mb-2"
            @click="printLabel()"
            :disabled="printingLabel"
            :aria-disabled="printingLabel"
          >
            Print Label
            <v-progress-circular
              indeterminate
              :size="20"
              color="primary"
              v-if="printingLabel"
              class="ml-2"
            ></v-progress-circular>
          </v-btn>
        </v-col>
        <v-col cols="12" sm="4" class="pa-2">
          <v-btn
            color="primary"
            block
            class="mb-2"
            @click="declareAnotherItem()"
          >
            Send Another Item
          </v-btn>
        </v-col>
        <v-col cols="12" sm="4" class="pa-2">
          <v-btn color="primary" block class="mb-2" @click="goHome()">
            Home
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import { createNamespacedHelpers } from "vuex";
import router from "../router";
import VueBarcode from 'vue-barcode';
const { mapGetters } = createNamespacedHelpers("declarations");

export default {
  name: "Summary",
  components: {
    'barcode': VueBarcode
  },
  data: () => ({}),
  computed: {
    ...mapGetters([
      "currentDeclaration",
      "countries",
      "requiresContentCategoryDescription",
    ]),
    expiresInDays() {
      return this.$store.getters.getDeclarationExpiryInDays;
    },
    printingLabel: {
      set(printing) {
        this.$store.dispatch("declarations/setPrintingLabel", printing);
      },
      get() {
        return this.$store.getters["declarations/printingLabel"];
      },
    },
  },
  methods: {
    getCountryNameById: function (id) {
      const country = this.countries.find(function (country) {
        return country.id === id;
      });
      return country ? country.name : "";
    },
    getCountryRequiresVoec: function (id) {
      if (id) {
        const country = this.countries.find(function (country) {
          return country.id === id;
        });
        return country ? country.requireVOEC: false;
      }
      return false;
    },    
    printLabel: function () {
      this.$store.dispatch("declarations/printLabel");
    },
    declareAnotherItem: function () {
      this.$store.dispatch(
        "declarations/startAnotherDeclarationForCurrentSender"
      );
    },
    goHome: function () {
      router.push({ name: "Intro" });
    },
  },
  mounted() {
    if (!this.currentDeclaration.declarationNumber) {
      //redirect to homepage
      router.push("/");
    }
  },
};
</script>

<style scoped>
.md-progress-bar {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
}

.summary-header {
  font-size: 1rem;
}
</style>
